import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Users, MapPin, Trash2, Pencil, Crown, UsersRound, LayoutGrid, List, Shield, X } from "lucide-react";
import { BoardIcon } from "@/lib/boardIcons";
import api from "@/lib/api";
import { toast } from "sonner";
import BoardDialog from "@/components/BoardDialog";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const BoardsList = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "admin";
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  // Bulk deletion (super-admin only, list view of shared boards)
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  // Shared-boards accordion view mode
  const [sharedView, setSharedView] = useState(
    () => (typeof window !== "undefined" && localStorage.getItem("boards.sharedView")) || "grid",
  );
  useEffect(() => { try { localStorage.setItem("boards.sharedView", sharedView); } catch {} }, [sharedView]);
  // Reset selection when the user switches away from the list view — checkboxes
  // don't exist in the grid layout so a stale selection would be confusing.
  useEffect(() => { if (sharedView !== "list") setSelectedIds(new Set()); }, [sharedView]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/boards");
      setBoards(r.data);
    } catch { toast.error("Errore caricamento schede"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const confirmDelete = async () => {
    try {
      await api.delete(`/boards/${toDelete.id}`);
      toast.success("Scheda eliminata");
      setToDelete(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const owned = boards.filter((b) => b.role_in_board === "owner");
  const shared = boards.filter((b) => b.role_in_board !== "owner");

  const toggleOne = (id) => setSelectedIds((s) => {
    const next = new Set(s); if (next.has(id)) next.delete(id); else next.add(id); return next;
  });
  const allChecked = shared.length > 0 && shared.every((b) => selectedIds.has(b.id));
  const someChecked = selectedIds.size > 0 && !allChecked;
  const toggleAll = () => setSelectedIds((s) => {
    if (allChecked) return new Set();
    return new Set(shared.map((b) => b.id));
  });

  const doBulkDelete = async () => {
    const ids = [...selectedIds];
    setBulkBusy(true);
    let ok = 0, ko = 0;
    // Fire deletes in parallel — the backend already lets admins bypass
    // require_owner, so all shared-board deletes go through.
    await Promise.all(ids.map((id) =>
      api.delete(`/boards/${id}`).then(() => { ok++; }).catch(() => { ko++; })
    ));
    setBulkBusy(false);
    setBulkConfirmOpen(false);
    setSelectedIds(new Set());
    if (ok) toast.success(`${ok} schede eliminate`);
    if (ko) toast.error(`${ko} eliminazioni fallite`);
    load();
  };

  /* -------- Grid card ----------------------------------------------------- */
  const BoardCard = ({ b, forceAdminDelete = false }) => {
    const isOwner = b.role_in_board === "owner";
    const canDelete = isOwner || (forceAdminDelete && isSuperAdmin);
    return (
      <div className="ios-card p-3 relative group transition-transform hover:-translate-y-0.5 overflow-hidden" data-testid={`board-card-${b.id}`}>
        {b.map_preview_url && (
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{ backgroundImage: `url(${b.map_preview_url})`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.5 }}
          />
        )}
        <div aria-hidden className="absolute inset-0 pointer-events-none bg-gradient-to-b from-white/55 via-white/40 to-white/75" />
        <div className="relative z-[1]">
          <div className="absolute top-0 right-0 flex items-center gap-1.5">
            {isOwner ? (
              <div className="flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[8px] uppercase tracking-widest font-semibold text-white shadow-sm" style={{ background: "linear-gradient(135deg, #f59e0b, #d97706)" }} title="Sei il proprietario di questa scheda" data-testid={`badge-owner-${b.id}`}>
                <Crown className="w-2.5 h-2.5" /><span>Proprietario</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[8px] uppercase tracking-widest font-semibold text-white shadow-sm" style={{ background: "linear-gradient(135deg, #34C759, #22a34c)" }} title="Sei collaboratore di questa scheda" data-testid={`badge-shared-${b.id}`}>
                <UsersRound className="w-2.5 h-2.5" /><span>Condivisa</span>
              </div>
            )}
          </div>

          {canDelete && (
            <div className="absolute bottom-0 right-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
              {isOwner && (
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 rounded-full bg-white/95 shadow-sm border border-slate-200"
                  onClick={() => { setEditing(b); setDialogOpen(true); }} data-testid={`edit-board-${b.id}`}>
                  <Pencil className="w-3 h-3" />
                </Button>
              )}
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 rounded-full text-rose-600 bg-white/95 shadow-sm border border-slate-200"
                onClick={() => setToDelete(b)} data-testid={`delete-board-${b.id}`}
                title={isOwner ? "Elimina scheda" : "Elimina scheda (admin)"}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          )}
          <Link to={`/boards/${b.id}`} className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-white/85 backdrop-blur border border-slate-200 select-none shadow-sm overflow-hidden shrink-0" data-testid={`board-emoji-${b.id}`}>
              <BoardIcon value={b.emoji} size={44} title={b.name} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-display font-bold text-sm leading-tight tracking-tight text-slate-900 pr-16 drop-shadow-sm truncate">{b.name}</h3>
              <div className="flex items-center gap-3 mt-1.5 text-[11px]">
                <div className="flex items-center gap-1 text-slate-700">
                  <MapPin className="w-3 h-3" />
                  <span className="font-mono-num font-semibold">{b.property_count || 0}</span>
                  <span className="text-slate-500">imm.</span>
                </div>
                <div className="flex items-center gap-1 text-slate-700">
                  <Users className="w-3 h-3" />
                  <span className="font-mono-num font-semibold">{(b.member_ids?.length || 0) + 1}</span>
                  <span className="text-slate-500">membri</span>
                </div>
                {!isOwner && b.owner && (
                  <div className="flex items-center gap-1 text-slate-500 truncate">
                    <span>·</span>
                    <span className="truncate">{b.owner.name || b.owner.email}</span>
                  </div>
                )}
              </div>
            </div>
          </Link>
        </div>
      </div>
    );
  };

  /* -------- Compact list row --------------------------------------------- */
  const BoardRow = ({ b }) => {
    const checked = selectedIds.has(b.id);
    return (
      <div
        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors border group ${checked ? "bg-rose-50/60 border-rose-200" : "border-transparent hover:bg-slate-50 hover:border-slate-200"}`}
        data-testid={`board-row-${b.id}`}
      >
        {isSuperAdmin && (
          <Checkbox
            checked={checked}
            onCheckedChange={() => toggleOne(b.id)}
            className="shrink-0"
            data-testid={`board-select-${b.id}`}
            aria-label={`Seleziona ${b.name}`}
          />
        )}
        <Link to={`/boards/${b.id}`} className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-slate-100 border border-slate-200 shrink-0">
            <BoardIcon value={b.emoji} size={26} title={b.name} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm text-slate-900 truncate">{b.name}</div>
            <div className="text-[11px] text-slate-500 truncate flex items-center gap-2">
              {b.owner && <span className="truncate">di <span className="font-medium text-slate-700">{b.owner.name || b.owner.email}</span></span>}
              <span className="text-slate-300">·</span>
              <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> <span className="font-mono-num">{b.property_count || 0}</span></span>
              <span className="text-slate-300">·</span>
              <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> <span className="font-mono-num">{(b.member_ids?.length || 0) + 1}</span></span>
            </div>
          </div>
        </Link>
        <span
          className="hidden sm:inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] uppercase tracking-widest font-bold text-white shrink-0"
          style={{ background: "linear-gradient(135deg, #34C759, #22a34c)" }}
        >
          <UsersRound className="w-2.5 h-2.5" /> Condivisa
        </span>
        {isSuperAdmin && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 rounded-full text-rose-600 hover:bg-rose-50 shrink-0 opacity-70 group-hover:opacity-100"
            onClick={() => setToDelete(b)}
            data-testid={`delete-board-row-${b.id}`}
            title="Elimina scheda (admin)"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-7xl mx-auto p-6">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Le tue schede</div>
            <h1 className="font-display font-bold text-4xl tracking-tighter leading-none mt-1">Schede di mappatura</h1>
            <p className="text-sm text-slate-500 mt-2">Crea schede separate, gestisci i tuoi immobili e collabora con altri utenti.</p>
          </div>
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="btn-new-board" className="rounded-full h-10 bg-[#007AFF] hover:bg-[#0060df] text-white">
            <Plus className="w-4 h-4 mr-1.5" /> Nuova scheda
          </Button>
        </div>

        {loading ? (
          <div className="text-slate-500 text-sm">Caricamento…</div>
        ) : boards.length === 0 ? (
          <div className="ios-card p-12 text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#E5F0FF] flex items-center justify-center mb-4">
              <MapPin className="w-6 h-6 text-[#12466F]" />
            </div>
            <div className="font-display font-bold text-xl">Non hai ancora schede</div>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">Crea la tua prima scheda di mappatura e inizia ad aggiungere immobili. Potrai invitare collaboratori dalla pagina Community.</p>
            <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="mt-5 rounded-full bg-[#007AFF] hover:bg-[#0060df]" data-testid="btn-first-board">
              <Plus className="w-4 h-4 mr-1.5" /> Crea la prima scheda
            </Button>
          </div>
        ) : (
          <>
            {owned.length > 0 && (
              <section className="mb-6">
                <h2 className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Le mie schede ({owned.length})</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="owned-boards-grid">
                  {owned.map((b) => <BoardCard key={b.id} b={b} />)}
                </div>
              </section>
            )}

            {shared.length > 0 && (
              <section className="mb-8" data-testid="shared-boards-section">
                <Accordion type="single" collapsible defaultValue="shared" className="ios-card px-4">
                  <AccordionItem value="shared" className="border-0">
                    <AccordionTrigger className="hover:no-underline py-3" data-testid="shared-boards-toggle">
                      <div className="flex items-center gap-2 text-left">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-xl text-white shrink-0"
                              style={{ background: "linear-gradient(135deg, #34C759, #22a34c)" }}>
                          <UsersRound className="w-3.5 h-3.5" />
                        </span>
                        <div>
                          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">Condivise con me</div>
                          <div className="text-sm font-semibold text-slate-800">
                            <span className="font-mono-num">{shared.length}</span> sched{shared.length === 1 ? "a" : "e"} in cui collabori
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-1 pb-4">
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Vista</div>
                        <div className="inline-flex rounded-full bg-slate-100 p-1" data-testid="shared-view-toggle">
                          <button
                            onClick={() => setSharedView("grid")}
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${sharedView === "grid" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
                            data-testid="shared-view-grid"
                          >
                            <LayoutGrid className="w-3 h-3" /> Griglia
                          </button>
                          <button
                            onClick={() => setSharedView("list")}
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${sharedView === "list" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
                            data-testid="shared-view-list"
                          >
                            <List className="w-3 h-3" /> Elenco
                          </button>
                        </div>
                      </div>

                      {isSuperAdmin && (
                        <div className="mb-3 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] uppercase tracking-widest font-bold text-amber-800 bg-amber-100 border border-amber-200">
                          <Shield className="w-3 h-3" /> Sei super-admin: puoi eliminare qualsiasi scheda
                        </div>
                      )}

                      {sharedView === "grid" ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="shared-boards-grid">
                          {shared.map((b) => <BoardCard key={b.id} b={b} forceAdminDelete />)}
                        </div>
                      ) : (
                        <>
                          {isSuperAdmin && (
                            <div className="flex items-center gap-3 px-3 py-2 mb-1 rounded-xl bg-slate-50 border border-slate-200" data-testid="shared-list-header">
                              <Checkbox
                                checked={allChecked ? true : (someChecked ? "indeterminate" : false)}
                                onCheckedChange={toggleAll}
                                data-testid="shared-select-all"
                                aria-label="Seleziona tutte"
                              />
                              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex-1">
                                {selectedIds.size > 0
                                  ? <>Selezionate: <span className="font-mono-num">{selectedIds.size}</span> / <span className="font-mono-num">{shared.length}</span></>
                                  : <>Seleziona tutte le schede</>}
                              </div>
                              {selectedIds.size > 0 && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setSelectedIds(new Set())}
                                    className="rounded-full h-7 text-slate-500 hover:text-slate-800"
                                    data-testid="shared-clear-selection"
                                  >
                                    <X className="w-3 h-3 mr-1" /> Deseleziona
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => setBulkConfirmOpen(true)}
                                    className="rounded-full h-7 bg-rose-600 hover:bg-rose-700 text-white"
                                    data-testid="shared-bulk-delete"
                                  >
                                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Elimina <span className="font-mono-num ml-1">{selectedIds.size}</span>
                                  </Button>
                                </>
                              )}
                            </div>
                          )}
                          <div className="rounded-xl border border-slate-100 divide-y divide-slate-100 bg-white" data-testid="shared-boards-list">
                            {shared.map((b) => <BoardRow key={b.id} b={b} />)}
                          </div>
                        </>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </section>
            )}
          </>
        )}

        {/* AnalyticsCharts moved to /dashboard (CommunityDashboard) — this
           page is just the boards list now. */}
      </main>

      <BoardDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditing(null); }}
        editing={editing}
        onSaved={load}
      />

      {/* Single-board delete confirm ------------------------------------- */}
      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la scheda "{toDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno eliminati tutti gli immobili contenuti ({toDelete?.property_count || 0}) e verrà revocato l'accesso ai collaboratori. Azione irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-rose-600 hover:bg-rose-700" onClick={confirmDelete} data-testid="confirm-delete-board">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirm (admin, list view) --------------------------- */}
      <AlertDialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare <span className="font-mono-num">{selectedIds.size}</span> schede selezionate?</AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione eliminerà definitivamente ogni scheda con tutti i suoi immobili e revocherà l'accesso ai relativi collaboratori. Non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" disabled={bulkBusy}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-full bg-rose-600 hover:bg-rose-700"
              onClick={doBulkDelete}
              disabled={bulkBusy}
              data-testid="confirm-bulk-delete"
            >
              {bulkBusy ? "Elimino…" : `Elimina ${selectedIds.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BoardsList;

